import {
  Payments_CardBrand as CardBrandEnum,
  Payments_PaymentProcessorId as PaymentProcessorId,
} from '__generated__/graphql-types';
import type { HostedFieldFieldOptions } from 'braintree-web';

import config from 'js/app/config';
import keysToConstants from 'js/lib/keysToConstants';
import path from 'js/lib/path';

import {
  braintree,
  mockPaymentProcessor,
  paypal,
  razorpay,
  stripe,
  stripeConsumer,
  zeroDollar,
} from 'bundles/payments-common/common/PaymentProcessorTypes';
import ProductType from 'bundles/payments-common/common/ProductType';
import { RAZORPAY_POLLING_TIMEOUT } from 'bundles/payments-common/utils/razorpayClientUtils';

// TODO(htran) add conditional for Braintree sandbox once BE support is completed
export const PAYPAL_CLIENT_ID = 'AcSMCRA5xVRrJ7HOPe7HFj1GicmGAUIKujZVOYnDD9_Qiz_HYSrx3RlAtVwf';

export const PAYPAL_CLIENT_ID_SANDBOX =
  'AfCnP2AYnrCwET3vPOJozQK-8aqDCeGmIzNEq1yqSzCK5N-WuS4hguS_e_QMGpgIMS4lnNVo55x8tTD4';

export const PAYMENTS_ENTERPRISE_RECAPTCHA_CHECKBOX_SITE_KEY = '6LenK_QZAAAAAOMKPEbpLQFzzuLgYZ5f93XLuBnN';

export const OWNERSHIP_POLLING_TIMEOUT = 'ownershipPollingTimeout';
export const RAZORPAY_INVALID_TOKEN_ERROR = 'razorpayInvalidTokenError';

const S3_BUCKET_PREFIX = 'growth/payments';

export const STRIPE_WALLET = 'STRIPE_WALLET';

// TODO (htran) consolidate 'wallet' type as subtype under braintree and paypal
// apple_pay and google_pay are local payment methods under stripe 'card'

// stripe is also being used for invoicing in WES
const PAYMENT_METHODS = keysToConstants([
  braintree,
  paypal,
  razorpay,
  'wallet',
  zeroDollar,
  mockPaymentProcessor,
  stripe,
  stripeConsumer,
  'invoice',
  'card',
  'apple_pay',
  'google_pay',
  'bancontact',
  'ideal',
  'link',
  'sepa_debit',
  'klarna',
  'pix',
  'stripe_wallet',
  'dlocal',
]);

// The S3 location is coursera-web-assets/images/signature/cc
const creditCardImagesBaseUrl = path.join(config.url.assets, 'images/signature/cc');

function ccImageURL(imageFile: string) {
  return path.join(creditCardImagesBaseUrl, imageFile);
}

const allCreditCards = {
  VISA: {
    name: 'Visa',
    url: ccImageURL('visa-2.png'),
  },
  MASTERCARD: {
    name: 'MasterCard',
    url: ccImageURL('mastercard-2.png'),
  },
  AMEX: {
    name: 'American Express',
    url: ccImageURL('amex-2.png'),
  },
  DISCOVER: {
    name: 'Discover',
    url: ccImageURL('discover.png'),
  },
  JCB: {
    name: 'JCB',
    url: ccImageURL('jcb.png'),
  },
  UPI: {
    name: 'Unified Payment Interface',
    url: ccImageURL('upi.png'),
  },
  NET_BANKING: {
    name: 'Net Banking',
    url: ccImageURL('netbanking.png'),
  },
};

const allCreditCardsStripe = {
  VISA_STRIPE: {
    name: 'visa',
    enumName: CardBrandEnum.Visa,
    url: ccImageURL('visa-2.png'),
  },
  MASTERCARD_STRIPE: {
    name: 'mastercard',
    enumName: CardBrandEnum.Mastercard,
    url: ccImageURL('mastercard-2.png'),
  },
  AMEX_STRIPE: {
    name: 'amex',
    enumName: CardBrandEnum.AmericanExpress,
    url: ccImageURL('amex-2.png'),
  },
  DISCOVER_STRIPE: {
    name: 'discover',
    enumName: CardBrandEnum.Discover,
    url: ccImageURL('discover.png'),
  },
  DINERS_STRIPE: {
    name: 'diners',
    enumName: CardBrandEnum.DinersClub,
    url: ccImageURL('diners.png'),
  },
  JCB_STRIPE: {
    name: 'jcb',
    enumName: CardBrandEnum.Jcb,
    url: ccImageURL('jcb.png'),
  },
  UNIONPAY_STRIPE: {
    name: 'unionpay',
    enumName: CardBrandEnum.UnionPay,
    url: ccImageURL('unionpay.png'),
  },
  EFTPOS_AU_STRIPE: {
    name: 'eftpos_au',
    enumName: CardBrandEnum.EftposAustralia,
    url: ccImageURL('credit.png'),
  },
  LINK_STRIPE: {
    name: 'link',
    enumName: CardBrandEnum.Other,
    url: ccImageURL('credit.png'),
  },
  OTHER_STRIPE: {
    name: 'unknown',
    enumName: CardBrandEnum.Other,
    url: ccImageURL('credit.png'),
  },
};

const { VISA, MASTERCARD, AMEX, DISCOVER, JCB, UPI, NET_BANKING } = allCreditCards;
const {
  VISA_STRIPE,
  MASTERCARD_STRIPE,
  AMEX_STRIPE,
  DISCOVER_STRIPE,
  DINERS_STRIPE,
  JCB_STRIPE,
  EFTPOS_AU_STRIPE,
  LINK_STRIPE,
  UNIONPAY_STRIPE,
  OTHER_STRIPE,
} = allCreditCardsStripe;

export const braintreeFieldIds: Partial<Record<keyof HostedFieldFieldOptions, string>> = {
  number: 'cc-number',
  expirationDate: 'cc-expiration-date',
  cvv: 'cc-cvv',
};

export const REFUND_POLICY_LINK = `${config.url.base}about/terms#payments-and-refund-policy`;

export const DISCLAIMER_LINKS = {
  TERMS_OF_USE: '/about/terms',
  REFUND_POLICY: REFUND_POLICY_LINK,
  PRIVACY_POLICY: '/about/privacy',
  CANCEL_SUBSCRIPTION: 'https://learner.coursera.help/hc/articles/216348123-Cancel-a-subscription',
};

export const CHECKOUT_INTENT_EXPIRED = 'checkoutIntentExpired';

const exported = {
  config,
  rootPath: '/payments',
  creditCardImagesBaseUrl,
  refundProductAction: 'Refund',
  buyProductAction: 'Buy',
  finaidAppMinWords: 10,
  finaidAppMinChars: 50,
  finaidAppMaxChars: 5000,
  daphneImg: 'https://s3.amazonaws.com/coursera/media/Daphne_Koller.png',
  andrewImg: 'https://s3.amazonaws.com/coursera/media/Andrew_Ng.png',

  // USD Currency Code
  VC_DEFAULT_CURRENCY: 840,

  VC_DEFAULT_COUNTRY_ALPHA2: 'US',
  VC_DEFAULT_CURRENCY_CODE: 'USD',
  epicNamespace: 'payments',
  cartUrl: 'checkout',
  cartIcon: 'bundles/payments-common/assets/cart_badged.png',
  paypalLogo: path.join(config.url.resource_assets, S3_BUCKET_PREFIX, 'paypal-logo.png'),
  cvvIcon: path.join(config.url.resource_assets, S3_BUCKET_PREFIX, 'cvv.png'),

  ProductType,

  ServicePeriod: {
    VERIFIED_CERTIFICATE: 180,
    SPECIALIZATION: 365,
  },

  fakeValidNonce: 'fake-valid-nonce',
  fakeValidAmexNonce: 'fake-valid-amex-nonce',
  amexTypeName: 'american-express',
  amexWalletTypeName: 'American Express',

  errorMapping: {
    fraud: 'card-declined-error',
    generalError: 'general-error',
    [RAZORPAY_POLLING_TIMEOUT]: RAZORPAY_POLLING_TIMEOUT,
    [OWNERSHIP_POLLING_TIMEOUT]: OWNERSHIP_POLLING_TIMEOUT,
    invalidRequest: 'general-error',
    invalidCard: 'card-invalid-error',
    'invalidCart.invalidCoupon': 'invalid-coupon',
    'invalidCart.expiredCart': 'expired-cart',
    'invalidCart.finAidCartNotValidForCheckout': 'finAidCartNotValidForCheckout',
    'invalidCoupon.alreadyRedeemed': 'invalid-coupon',
    'invalidCoupon.expired': 'invalid-coupon',
    'invalidPromotion.redemptionLimitReached': 'promotion-redemption-limit-reached',
    'invalidPromotion.currencyNotSupported': 'general-error',
    'invalidPromoCode.exhausted': 'promo-code-exhausted',
    'invalidPromoCode.expired': 'promo-code-unavailable',
    'invalidPromoCode.unknown': 'promo-code-unavailable',
    'invalidCurrency.conflictCurrency': 'stripe-invalid-currency',
    unverifiedEmail: 'unverified-email',
    issuerDecline: 'card-declined-error',
    paypalBuyerRevokedPaymentAuthorization: 'paypal-buyer-revoked-payment-authorization-error',
    paypalFailure: 'paypal-error',
    paypalLogin: 'paypal-missing-login',
    unknown: 'card-declined-error',
    timeout: 'timeout-error',
    internalErrorProduct: 'general-error',
    internalErrorPayment: 'general-error',
    paymentProcessorError: 'general-error',
    paymentWalletBadRequest: 'card-declined-error',
    invalidFields: 'card-fields-error',
    CONTRACT_PAYMENT_ERROR: 'enterprise-contract-error',
    INVALID_CONTRACT_BILLING_COUNTRY_INPUT: 'invalid-contract-billing-country-input',
    stripeError: 'stripe-error',
    stripeValidationError: 'stripe-validation-error',
    stripeInvalidRequest: 'stripe-invalid-request-error',
    stripeInvalidParameter: 'stripe-invalid-parameter-error',
    incompleteIntent: 'incomplete-intent',
    checkoutIntentMissingAddress: 'checkout-intent-missing-address',
    taxEstimateError: 'tax-estimate-error',
    [CHECKOUT_INTENT_EXPIRED]: 'checkout-intent-expired',
    checkoutIntentReinitialize: 'checkout-intent-reinitialize',
    ENTERPRISE_CONTRACT_REFRESH_ERROR: 'enterprise-contract-refresh-error',
    issuerDeclineGeneric: 'stripe-decline-bank-try-again', // do_not_honor, transaction_not_allowed, generic_decline
    riskGatewayRejected: 'stripe-decline-bank-try-again', // highest_risk_level
    restrictedCard: 'stripe-decline-bank-try-again', // requested_block_on_incorrect_cvc
    authenticationRequired: 'stripe-decline-bank-try-again', // authentication_required
    incorrectCvc: 'stripe-decline-incorrect-details', // incorrect_cvc
    invalidCardNumber: 'stripe-decline-incorrect-details', // incorrect_number
    insufficientFunds: 'stripe-decline-insufficient-funds', // insufficient_funds
    networkError: 'stripe-decline-temporary-issue', // try_again_later, reenter_transaction
    limitExceeded: 'stripe-decline-do-not-retry', // card_velocity_exceeded, previously_declined_do_not_retry
    stripePayFailure: 'general-error',
    CONTRACT_ORDER_CHECKOUT_BAD_REQUEST: 'general-error',
    invoiceValidationError: 'invoice-validation-error',
    invoiceGeneralError: 'invoice-general-error',
    dlocalInvalidFields: 'dlocal-fields-error',
    dlocalInvalidPayerInfo: 'dlocal-invalid-payer-info',
    dlocalInvalidPayerName: 'dlocal-invalid-payer-name',
    dlocalInvalidPayerEmail: 'dlocal-invalid-payer-email',
    dlocalInvalidPayerPhone: 'dlocal-invalid-payer-phone',
    dlocalInvalidPayerDocument: 'dlocal-invalid-payer-document',
    dlocalInvalidPayerAccount: 'dlocal-invalid-payer-account',
    razorpayInvalidTokenError: 'razorpay-invalid-token-error',
  } as const,

  errorMessageSubstringsToIgnoreForSentry: ['issuerDecline'],

  acceptedCreditCards: [VISA, MASTERCARD, AMEX, DISCOVER, JCB],
  acceptedCreditCardsAmexCampaign: [AMEX],
  acceptedCreditCardsStripe: [
    VISA_STRIPE,
    MASTERCARD_STRIPE,
    AMEX_STRIPE,
    DISCOVER_STRIPE,
    DINERS_STRIPE,
    JCB_STRIPE,
    EFTPOS_AU_STRIPE,
    LINK_STRIPE,
    UNIONPAY_STRIPE,
    OTHER_STRIPE,
  ],

  acceptedCreditCardsStandaloneCourseIN: [VISA, MASTERCARD, AMEX, UPI, NET_BANKING],
  acceptedCreditCardsSubscriptionIN: [VISA, MASTERCARD],

  paymentMethods: PAYMENT_METHODS,

  premiumExperienceVariants: {
    premiumCourse: 'PremiumCourse',
    premiumGrading: 'PremiumGrading',
    baseVariant: 'BaseVariant',
  },

  subscriptionBillingType: keysToConstants(['MONTHLY', 'ANNUAL', 'BIANNUAL']),

  freeTrial: {
    numDays: 7,
    amexCampaignNumDays: 60,
    preEnrollmentNumDays: 14,
  },

  // 7-day free trial subscription BE promo id
  subscriptionTrialPromoId: 854,

  braintreeVerifiedLink: 'https://www.braintreegateway.com/merchants/2v7ggjcrvs26zssz/verified',

  creditCardPaymentMethods: [PAYMENT_METHODS.braintree, PAYMENT_METHODS.stripeConsumer, PAYMENT_METHODS.stripe],

  braintreeFieldIds,

  PAYPAL_CLIENT_ID,
};

export const localPaymentMethodsList = ['card', 'bancontact', 'ideal', 'link', 'sepa_debit', 'klarna', 'pix']; // TODO: IP-to-payment methods mapping

export const VALIDATION_ERROR_CODES = [
  'invalidCart.expiredCart',
  'invalidCoupon.alreadyRedeemed',
  'invalidCoupon.expired',
  'invalidPromotion.redemptionLimitReached',
  'invalidPromotion.currencyNotSupported',
  'invalidPromoCode.exhausted',
  'invalidPromoCode.expired',
  'invalidPromoCode.unknown',
] as const;

export const NON_ACTIONABLE_CART_ERROR_CODES = [
  'invalid-coupon',
  'promotion-redemption-limit-reached',
  'promo-code-exhausted',
  'promo-code-unavailable',
  'expired-cart',
  'stripe-invalid-currency',
] as const;

export const localPaymentMethods = [
  PAYMENT_METHODS.card,
  PAYMENT_METHODS.apple_pay,
  PAYMENT_METHODS.google_pay,
  PAYMENT_METHODS.bancontact,
  PAYMENT_METHODS.ideal,
  PAYMENT_METHODS.link,
  PAYMENT_METHODS.sepa_debit,
  PAYMENT_METHODS.klarna,
  PAYMENT_METHODS.pix,
] as Array<keyof typeof paymentMethods>;

export const localPaymentMethodsMapping = {
  [PAYMENT_METHODS.card]: 'Card',
  [PAYMENT_METHODS.apple_pay]: 'Apple Pay',
  [PAYMENT_METHODS.google_pay]: 'Google Pay',
  [PAYMENT_METHODS.bancontact]: 'Bancontact',
  [PAYMENT_METHODS.ideal]: 'iDeal',
  [PAYMENT_METHODS.link]: 'Link',
  [PAYMENT_METHODS.sepa_debit]: 'SEPA Direct Debit',
  [PAYMENT_METHODS.klarna]: 'Klarna',
  [PAYMENT_METHODS.pix]: 'PIX',
};

export const STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS = [
  PaymentProcessorId.StripeConsumer,
  PaymentProcessorId.StripeConsumerSandbox,
  PaymentProcessorId.StripeBvConsumer,
  PaymentProcessorId.StripeBvConsumerSandbox,
];

export const klarnaSupportedEUCountries = [
  'AT',
  'BE',
  'DE',
  'DK',
  'ES',
  'FI',
  'IE',
  'IT',
  'NL',
  'NO',
  'SE',
  'FR',
  'CZ',
  'GR',
  'PL',
  'PT',
  'CH',
];

export const klarnaSupportedCountries = ['GB', 'US', ...klarnaSupportedEUCountries];

export default exported;
export { config, creditCardImagesBaseUrl, ProductType };

export const {
  rootPath,
  refundProductAction,
  buyProductAction,
  finaidAppMinWords,
  finaidAppMinChars,
  finaidAppMaxChars,
  daphneImg,
  andrewImg,
  VC_DEFAULT_CURRENCY,
  VC_DEFAULT_COUNTRY_ALPHA2,
  VC_DEFAULT_CURRENCY_CODE,
  epicNamespace,
  cartUrl,
  cartIcon,
  paypalLogo,
  cvvIcon,
  ServicePeriod,
  fakeValidNonce,
  fakeValidAmexNonce,
  amexTypeName,
  amexWalletTypeName,
  errorMapping,
  errorMessageSubstringsToIgnoreForSentry,
  acceptedCreditCards,
  acceptedCreditCardsStripe,
  acceptedCreditCardsStandaloneCourseIN,
  acceptedCreditCardsSubscriptionIN,
  paymentMethods,
  premiumExperienceVariants,
  subscriptionBillingType,
  freeTrial,
  subscriptionTrialPromoId,
  braintreeVerifiedLink,
  creditCardPaymentMethods,
} = exported;
